import { useEffect, useMemo, useRef, useState } from 'react'

import type {
    CarrierLegacyUnitCandidate,
    CarrierLegacyUnitSuggestion,
} from '@/lib/api/carrier-shipment-api'

import type { CarrierLegacyCommuneBlock } from '@/lib/carrier-legacy-commune-tracking'
import { useCarrierLegacyUnits } from '@/hooks/use-carrier-shipment-query'
import { useDebounced } from '@/hooks/use-debounced'
import {
    CARRIER_LEGACY_UNIT_NOT_CANDIDATE,
    CARRIER_LEGACY_UNIT_REQUIRED,
} from '@/lib/api/carrier-shipment-api'
import { ApiError } from '@/lib/api/client'
import { resolveApiErrorMessage } from '@/lib/api-error-messages'
import { captureCarrierLegacyCommune } from '@/lib/carrier-legacy-commune-tracking'
import { m } from '@/lib/paraglide/messages'

/**
 * The operator's choice of old commune for the printed label.
 *
 * A suggestion the system recognised (single / previous_order / address)
 * counts as chosen from the start. A `default` guess does not: the operator
 * must press "Đúng xã này" or pick a value. `resetKey` is the receiver's ward
 * code, so a different ward drops both the pick and any error.
 */
/** Id of the commune picker, so a refusal can move focus to it. */
export const LEGACY_UNIT_FIELD_ID = 'carrier-legacy-ward'

export function useLegacyUnitChoice(
    suggestion: CarrierLegacyUnitSuggestion | null | undefined,
    resetKey: string,
) {
    const [picked, setPicked] = useState<{ key: string; id: number } | null>(
        null,
    )
    const [failure, setFailure] = useState<{
        key: string
        message: string
    } | null>(null)

    const candidates = suggestion?.candidates ?? []
    const others = suggestion?.other_candidates ?? []
    const known = (id: number | null | undefined): id is number =>
        id != null &&
        [...candidates, ...others].some((unit) => unit.vtp_ward_id === id)

    const pickedId = picked?.key === resetKey ? picked.id : undefined
    const suggestedId = known(suggestion?.suggested_vtp_ward_id)
        ? suggestion.suggested_vtp_ward_id
        : undefined
    const value = known(pickedId) ? pickedId : suggestedId
    const source = suggestion?.source ?? null
    const recognised =
        source === 'single' ||
        source === 'previous_order' ||
        source === 'address'
    const chosen = known(pickedId)
    const confirmed = value !== undefined && (chosen || recognised)

    const caption = chosen
        ? m.carrier_legacy_caption_chosen()
        : source === 'single'
          ? m.carrier_legacy_caption_single()
          : source === 'previous_order'
            ? m.carrier_legacy_caption_previous_order()
            : source === 'address'
              ? m.carrier_legacy_caption_address()
              : m.carrier_legacy_caption_default()

    const pick = (id: number) => {
        setPicked({ key: resetKey, id })
        setFailure(null)
    }
    const candidateCount = candidates.length
    const otherCount = others.length
    const listSizes = { source, candidateCount, otherCount }
    const visible = candidates.length > 0
    const addressWard = addressWardOf(suggestion, resetKey)
    const warned = addressWard !== null
    // The row is reported once per suggestion for a ward, not on every render
    // or refetch of the same one.
    const shownKey = visible ? `${resetKey}|${source}|${warned}` : null
    const reportedKey = useRef<string | null>(null)
    useEffect(() => {
        if (shownKey === null || reportedKey.current === shownKey) return
        reportedKey.current = shownKey
        captureCarrierLegacyCommune({
            action: 'shown',
            source,
            candidateCount,
            otherCount,
            addressWard: warned,
        })
    }, [shownKey, source, candidateCount, otherCount, warned])
    const choose = (id: number) => {
        pick(id)
        captureCarrierLegacyCommune({
            action: 'changed',
            ...listSizes,
            toOtherList: others.some((unit) => unit.vtp_ward_id === id),
            toSuggestion: id === suggestedId,
        })
    }
    const selected: CarrierLegacyUnitCandidate | undefined = [
        ...candidates,
        ...others,
    ].find((unit) => unit.vtp_ward_id === value)

    // A commune found in another ward is kept for that ward: the receiver
    // moves there, the suggestion is refetched for it, and the pick survives.
    const chooseInWard = (
        wardKey: string,
        id: number,
        fromAddressWard = false,
    ) => {
        setPicked({ key: wardKey, id })
        setFailure(null)
        captureCarrierLegacyCommune({
            action: 'changed',
            ...listSizes,
            toOtherWard: true,
            ...(fromAddressWard ? { fromAddressWard } : {}),
        })
    }

    const reject = (message: string, reason: CarrierLegacyCommuneBlock) => {
        setFailure({ key: resetKey, message })
        document.getElementById(LEGACY_UNIT_FIELD_ID)?.focus()
        captureCarrierLegacyCommune({ action: 'blocked', source, reason })
    }

    return {
        visible,
        candidates,
        others,
        addressWard,
        value,
        selected,
        confirmed,
        caption,
        error: failure?.key === resetKey ? failure.message : null,
        choose,
        chooseInWard,
        confirm: () => {
            if (value === undefined) return
            pick(value)
            captureCarrierLegacyCommune({ action: 'confirmed', ...listSizes })
        },
        /** Show `message` under the row and move focus to the select. */
        reject,
        /** Submit guard: false (and an inline error) while a guess is unconfirmed. */
        requireChoice: (): boolean => {
            if (!visible || confirmed) return true
            reject(m.carrier_legacy_required(), 'unconfirmed')
            return false
        },
    }
}

/** The address's own ward when it is not `wardCode`, ready to move to. */
function addressWardOf(
    suggestion: CarrierLegacyUnitSuggestion | null | undefined,
    wardCode: string,
) {
    const unit = suggestion?.address_ward
    // Without a ward (the province was just changed) the server answers for
    // the saved one, so its warning belongs to a place already left.
    if (
        !wardCode ||
        !unit?.current_ward_code ||
        !unit.current_ward_name ||
        unit.current_ward_code === wardCode
    ) {
        return null
    }
    return {
        ...unit,
        current_ward_code: unit.current_ward_code,
        current_ward_name: unit.current_ward_name,
    }
}

interface FieldOptions {
    orderId: string
    active: boolean
    /** The receiver's ward code; a different ward drops the operator's pick. */
    wardCode: string
    /** Receiver edits not yet saved on the order; undefined = as saved. */
    editedWardCode?: string
    editedAddress?: string
}

/**
 * The legacy-commune field of the booking form: fetches the suggestion for the
 * receiver as typed (debounced per string, so the query key stays stable),
 * keeps the operator's choice, and guards the submit.
 */
export function useCarrierLegacyUnit({
    orderId,
    active,
    wardCode,
    editedWardCode,
    editedAddress,
}: FieldOptions) {
    const debouncedWard = useDebounced(editedWardCode)
    const debouncedAddress = useDebounced(editedAddress)
    const params = useMemo(
        () => ({
            ...(debouncedWard !== undefined
                ? { ward_code: debouncedWard }
                : {}),
            ...(debouncedAddress !== undefined
                ? { receiver_address: debouncedAddress }
                : {}),
        }),
        [debouncedWard, debouncedAddress],
    )
    const query = useCarrierLegacyUnits(orderId, params, active)
    const choice = useLegacyUnitChoice(query.data, wardCode)
    // A suggestion computed for older input must not be sent with newer input.
    const updating =
        debouncedWard !== editedWardCode ||
        debouncedAddress !== editedAddress ||
        query.isFetching

    return {
        ...choice,
        // The warning offers a ward to move to; one computed for older input
        // could belong to a province the receiver has just left, and a
        // cleared address is answered with the saved one.
        addressWard:
            updating || editedAddress?.trim() === ''
                ? null
                : choice.addressWard,
        loadFailed: query.isError,
        retry: () => void query.refetch(),
        /** Id to send with a quote or dispatch; undefined = send nothing. */
        wardId: choice.visible ? choice.value : undefined,
        /** Request fields to spread into a quote or dispatch body. */
        payload:
            choice.visible && choice.value !== undefined
                ? { vtp_legacy_ward_id: choice.value }
                : {},
        /** Submit guard: false (with an inline message) until the choice is safe to send. */
        requireChoice: (): boolean => {
            if (updating && choice.visible) {
                choice.reject(m.carrier_legacy_updating(), 'updating')
                return false
            }
            return choice.requireChoice()
        },
        /** True when a 422 about the commune was shown on the row itself. */
        showRejection: (err: unknown): boolean => {
            if (
                !choice.visible ||
                !(err instanceof ApiError) ||
                (err.code !== CARRIER_LEGACY_UNIT_REQUIRED &&
                    err.code !== CARRIER_LEGACY_UNIT_NOT_CANDIDATE)
            ) {
                return false
            }
            choice.reject(
                resolveApiErrorMessage(err.code, err.params) ??
                    (err.message || m.carrier_legacy_required()),
                err.code === CARRIER_LEGACY_UNIT_REQUIRED
                    ? 'required'
                    : 'not_candidate',
            )
            return true
        },
    }
}
